import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ZoomIn } from 'lucide-react';
import AddToCartModal from '../components/AddToCartModal';
import ReviewSection from '../components/ReviewSection';
import ImageZoomLightbox from '../components/ImageZoomLightbox';
import RestockAlertModal from '../components/RestockAlertModal';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { getColorHex, isLightColor } from '../utils/colorMap';
import { resolveImageUrl, optimizedImageUrl } from '../utils/imageUrl';
import { ViewersBadge } from '../components/ViewersBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GarmentDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [garment, setGarment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddToCartModal, setShowAddToCartModal] = useState(false);
  const [showRestockModal, setShowRestockModal] = useState(false);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const touchX = useRef(null);
  const swiped = useRef(false);

  useEffect(() => {
    fetchGarment();
  }, [id]);

  const fetchGarment = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/garments/${id}`);
      setGarment(response.data);
    } catch (error) {
      console.error('Error fetching garment:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el producto',
        variant: 'destructive',
      });
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!garment) return null;

  const gallery =
    Array.isArray(garment.images) && garment.images.length > 0
      ? garment.images
      : garment.image_url
      ? [garment.image_url]
      : [];

  return (
    <div className="min-h-screen bg-white text-black flex flex-col">
      <Header />

      <main className="flex-1 max-w-[1440px] mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Breadcrumb / Back */}
        <button
          onClick={() => navigate(-1)}
          className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-500 hover:text-black transition-colors flex items-center gap-1.5 mb-8"
          data-testid="back-to-catalog"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
          Volver
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-16 mb-16">
          {/* Gallery */}
          <div>
            {/* Main image */}
            <button
              onClick={() => { if (!swiped.current) setZoomOpen(true); swiped.current = false; }}
              onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                const dx = e.changedTouches[0].clientX - (touchX.current ?? 0);
                if (Math.abs(dx) > 40 && gallery.length > 1) {
                  swiped.current = true;
                  setActiveImageIdx((i) => (i + (dx < 0 ? 1 : -1) + gallery.length) % gallery.length);
                }
              }}
              className="relative block w-full aspect-[4/5] bg-neutral-100 overflow-hidden group cursor-zoom-in"
              data-testid="main-image-card"
            >
              {gallery.map((url, idx) => (
                <img
                  key={url + idx}
                  src={optimizedImageUrl(url, 800)}
                  srcSet={`${optimizedImageUrl(url, 800)} 800w, ${optimizedImageUrl(url, 1200)} 1200w`}
                  sizes="(max-width: 1024px) 100vw, 55vw"
                  alt={`${garment.name} ${idx + 1}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-[opacity,transform] duration-300 group-hover:scale-105 ${
                    idx === activeImageIdx ? 'opacity-100' : 'opacity-0'
                  }`}
                  data-testid={idx === activeImageIdx ? 'main-product-image' : `gallery-image-${idx}`}
                  fetchPriority={idx === 0 ? 'high' : 'low'}
                  decoding="async"
                  onError={(e) => { if (!e.currentTarget.dataset.fallback) { e.currentTarget.dataset.fallback = '1'; e.currentTarget.srcset = ''; e.currentTarget.src = resolveImageUrl(url); } }}
                />
              ))}
              <span className="absolute top-4 right-4 bg-white p-2.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="w-4 h-4 text-black" strokeWidth={1.5} />
              </span>
              <ViewersBadge garmentId={garment.id} />
            </button>

            {/* Thumbnails */}
            {gallery.length > 1 && (
              <div className="grid grid-cols-5 gap-1.5 mt-2" data-testid="gallery-thumbnails">
                {gallery.map((url, idx) => (
                  <button
                    key={url + idx}
                    type="button"
                    onClick={() => setActiveImageIdx(idx)}
                    className={`bg-neutral-100 aspect-[3/4] overflow-hidden transition-all ${
                      idx === activeImageIdx ? 'ring-2 ring-black' : 'opacity-70 hover:opacity-100'
                    }`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img
                      src={optimizedImageUrl(url, 400)}
                      alt={`${garment.name} miniatura ${idx + 1}`}
                      className="w-full h-full object-cover"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Product video (autoplay muted loop, no sound controls) */}
            {garment.video_url && (
              <div className="mt-4" data-testid="product-video-wrapper">
                <video
                  src={resolveImageUrl(garment.video_url)}
                  poster={garment.video_poster_url && garment.video_poster_url.startsWith('/') ? resolveImageUrl(garment.video_poster_url) : undefined}
                  className="w-full aspect-[4/5] object-cover bg-black"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  disablePictureInPicture
                  disableRemotePlayback
                  controlsList="nodownload nofullscreen noremoteplayback"
                  data-testid="product-video"
                  onLoadedMetadata={(e) => { e.currentTarget.muted = true; e.currentTarget.volume = 0; }}
                />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-neutral-500 mb-3">
              {garment.gender} · {garment.category}
            </p>

            <h1
              className="text-3xl sm:text-4xl font-black uppercase tracking-tight leading-tight mb-4"
              data-testid="product-name"
            >
              {garment.name}
            </h1>

            {garment.price != null && (
              garment.sale_price != null && garment.sale_price < garment.price ? (
                <div className="flex items-baseline gap-3 mb-8" data-testid="product-price">
                  <p className="text-2xl font-semibold text-red-600">{garment.sale_price.toFixed(2)}€</p>
                  <p className="text-lg text-neutral-400 line-through">{garment.price.toFixed(2)}€</p>
                  <span className="text-xs font-bold uppercase tracking-widest bg-red-600 text-white px-2 py-1">
                    -{Math.round(((garment.price - garment.sale_price) / garment.price) * 100)}%
                  </span>
                </div>
              ) : (
                <p className="text-2xl font-semibold mb-8" data-testid="product-price">
                  {garment.price.toFixed(2)}€
                </p>
              )
            )}

            {garment.inspirational_phrase && (
              <p className="text-sm text-neutral-600 italic border-l-2 border-black pl-4 mb-6">
                {garment.inspirational_phrase}
              </p>
            )}

            {garment.short_description && (
              <p className="text-sm text-neutral-700 leading-relaxed mb-8">
                {garment.short_description}
              </p>
            )}

            {/* Colors preview */}
            {garment.colors && garment.colors.length > 0 && (
              <div className="mb-6">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-500 mb-3">
                  Colores disponibles
                </p>
                <div className="flex flex-wrap gap-2">
                  {garment.colors.map((color, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 border border-neutral-200 px-3 py-1.5 text-xs"
                      title={color}
                    >
                      <span
                        className="w-3.5 h-3.5 rounded-full inline-block"
                        style={{
                          backgroundColor: getColorHex(color),
                          border: isLightColor(color) ? '1px solid #D1D5DB' : 'none',
                        }}
                      />
                      <span className="uppercase tracking-wide">{color}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add to Cart CTA + Restock Alert */}
            <button
              onClick={() => setShowAddToCartModal(true)}
              className="w-full bg-black text-white hover:bg-neutral-800 py-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors mb-3"
              data-testid="add-to-cart-detail"
            >
              Añadir al carrito
            </button>
            <button
              onClick={() => setShowRestockModal(true)}
              className="w-full bg-white text-black border border-black hover:bg-black hover:text-white py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors mb-3"
              data-testid="restock-alert-btn"
            >
              🔔 Avísame cuando vuelva
            </button>

            {/* Accordion: Description, Materials, Shipping */}
            <div className="mt-10 border-t border-neutral-200">
              {garment.description && (
                <details className="group border-b border-neutral-200">
                  <summary className="flex items-center justify-between cursor-pointer py-5 text-[11px] font-bold uppercase tracking-[0.2em]">
                    Descripción
                    <span className="text-lg group-open:rotate-45 transition-transform duration-200">
                      +
                    </span>
                  </summary>
                  <p className="pb-5 text-sm text-neutral-700 leading-relaxed">
                    {garment.description}
                  </p>
                </details>
              )}
              {garment.materials && (
                <details className="group border-b border-neutral-200">
                  <summary className="flex items-center justify-between cursor-pointer py-5 text-[11px] font-bold uppercase tracking-[0.2em]">
                    Materiales
                    <span className="text-lg group-open:rotate-45 transition-transform duration-200">
                      +
                    </span>
                  </summary>
                  <p className="pb-5 text-sm text-neutral-700 leading-relaxed">
                    {garment.materials}
                  </p>
                </details>
              )}
              <details className="group border-b border-neutral-200">
                <summary className="flex items-center justify-between cursor-pointer py-5 text-[11px] font-bold uppercase tracking-[0.2em]">
                  Envío y devoluciones
                  <span className="text-lg group-open:rotate-45 transition-transform duration-200">
                    +
                  </span>
                </summary>
                <p className="pb-5 text-sm text-neutral-700 leading-relaxed">
                  Envío gratis en pedidos superiores a 80€. Devoluciones gratuitas en 15 días. Consulta nuestra{' '}
                  <button
                    onClick={() => navigate('/devoluciones')}
                    className="underline hover:no-underline"
                  >
                    política de devoluciones
                  </button>
                  .
                </p>
              </details>
            </div>
          </div>
        </div>

        {/* Reviews */}
        <ReviewSection garmentId={garment.id} garmentName={garment.name} />
      </main>

      <Footer />

      <AddToCartModal
        isOpen={showAddToCartModal}
        onClose={() => setShowAddToCartModal(false)}
        garment={garment}
      />

      <RestockAlertModal
        isOpen={showRestockModal}
        onClose={() => setShowRestockModal(false)}
        garment={garment}
        sizes={garment.sizes || []}
        colors={garment.colors || []}
      />

      <ImageZoomLightbox
        images={gallery}
        startIndex={activeImageIdx}
        isOpen={zoomOpen}
        onClose={() => setZoomOpen(false)}
        alt={garment.name}
      />

      <Toaster />
    </div>
  );
};

export default GarmentDetailPage;
